import { Heart, ShoppingCart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/components/auth/AuthProvider";
import { useMutation, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface ProductActionsProps {
  productId: string;
  price: number;
}

const ProductActions = ({ productId, price }: ProductActionsProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user } = useAuth();

  console.log("Checking wishlist for product:", productId, "and user:", user?.id);

  // Query to check if product is in wishlist
  const { data: wishlistItem, refetch: refetchWishlist } = useQuery({
    queryKey: ['wishlist', productId, user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      console.log("Fetching wishlist status...");
      const { data, error } = await supabase
        .from('wishlists')
        .select('id')
        .eq('product_id', productId)
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (error) {
        console.error("Error fetching wishlist:", error);
        throw error;
      }
      
      console.log("Wishlist data:", data);
      return data;
    },
    enabled: !!user?.id
  });

  const addToWishlistMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error('Missing user ID');
      
      console.log("Adding to wishlist...");
      const { error } = await supabase
        .from('wishlists')
        .insert({ product_id: productId, user_id: user.id });

      if (error) throw error;
    },
    onSuccess: () => {
      refetchWishlist();
      toast({
        title: t('products.addedToWishlist'),
        description: t('products.productAddedToWishlist'),
      });
    }
  });

  const removeFromWishlistMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error('Missing user ID');
      
      console.log("Removing from wishlist...");
      const { error } = await supabase
        .from('wishlists')
        .delete()
        .eq('product_id', productId)
        .eq('user_id', user.id);

      if (error) {
        console.error("Error removing from wishlist:", error);
        throw error;
      }
    },
    onSuccess: () => {
      refetchWishlist();
      toast({
        title: t('products.removedFromWishlist'),
        description: t('products.productRemovedFromWishlist'),
      });
    }
  });

  const handleWishlistAction = () => {
    if (!user) {
      toast({
        title: t('auth.required'),
        description: t('auth.pleaseLoginFirst'),
      });
      return;
    }

    if (wishlistItem) {
      removeFromWishlistMutation.mutate();
    } else {
      addToWishlistMutation.mutate();
    }
  };

  return (
    <div className="flex items-center gap-4">
      <Button 
        variant={wishlistItem ? "default" : "outline"}
        onClick={handleWishlistAction}
        className="flex items-center gap-2"
      >
        <Heart className={`w-4 h-4 ${wishlistItem ? "fill-current" : ""}`} />
        {wishlistItem ? t('products.removeFromWishlist') : t('products.addToWishlist')}
      </Button>
      
      <div className="flex items-center gap-2">
        <ShoppingCart className="w-4 h-4" />
        <span>{t('products.price')}: ${price}</span>
      </div>
    </div>
  );
};

export default ProductActions;